import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { syncConfigService } from "../services/sync-config.service";
import {
  CreateSyncConfigDto,
  UpdateSyncConfigDto,
  SyncConfigDto,
  BackfillRequestDto,
  SyncQueueDto,
  SyncRunDto,
} from "../types/dtos/sync";
import { useOrganization } from "../../contexts/OrganizationContext";
import { isAxiosError } from "axios";

const SYNC_CONFIGS_KEY = "sync-configs";

const shouldNotRetry403 = (_failureCount: number, error: unknown) => {
  if (isAxiosError(error) && error.response?.status === 403) return false;
  return true;
};

export const useSyncConfigs = () => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [SYNC_CONFIGS_KEY, selectedOrganization?.id],
    queryFn: () => syncConfigService.findAll().then((res) => res.data),
    enabled: !!selectedOrganization?.id,
    staleTime: 30000,
    retry: shouldNotRetry403,
  });
};

export const useSyncSourceOptions = () => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [SYNC_CONFIGS_KEY, "source-options", selectedOrganization?.id],
    queryFn: () => syncConfigService.getSourceOptions().then((res) => res.data),
    enabled: !!selectedOrganization?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: shouldNotRetry403,
  });
};

export const useSyncConfig = (id: string | null) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [SYNC_CONFIGS_KEY, selectedOrganization?.id, id],
    queryFn: () => syncConfigService.findById(id!).then((res) => res.data),
    enabled: !!id && !!selectedOrganization?.id,
    retry: shouldNotRetry403,
  });
};

export const useCreateSyncConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSyncConfigDto) =>
      syncConfigService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SYNC_CONFIGS_KEY] });
    },
  });
};

export const useUpdateSyncConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSyncConfigDto }) =>
      syncConfigService.update(id, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SYNC_CONFIGS_KEY] });
    },
  });
};

export const useDeleteSyncConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => syncConfigService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SYNC_CONFIGS_KEY] });
    },
  });
};

export const useBackfillSyncConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BackfillRequestDto }) =>
      syncConfigService.backfill(id, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SYNC_CONFIGS_KEY] });
    },
  });
};

export type { SyncConfigDto };

const SYNC_QUEUE_KEY = "sync-queue";
const SYNC_RUNS_KEY = "sync-runs";

interface SpringPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export const useSyncQueueItems = (configId: string | null, page: number, size: number) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [SYNC_QUEUE_KEY, configId, page, size],
    queryFn: () =>
      syncConfigService
        .getQueueItems(configId!, page, size)
        .then((res) => res.data as SpringPage<SyncQueueDto>),
    enabled: !!configId && !!selectedOrganization?.id,
    staleTime: 15000,
    retry: shouldNotRetry403,
  });
};

export const useSyncRuns = (
  configId: string | null,
  excludeIdle: boolean,
  page: number,
  size: number
) => {
  const { selectedOrganization } = useOrganization();
  return useQuery({
    queryKey: [SYNC_RUNS_KEY, configId, excludeIdle, page, size],
    queryFn: () =>
      syncConfigService
        .getRuns(configId!, page, size, excludeIdle)
        .then((res) => res.data as SpringPage<SyncRunDto>),
    enabled: !!configId && !!selectedOrganization?.id,
    staleTime: 15000,
    retry: shouldNotRetry403,
  });
};

export const useRequestSyncRunReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, runId }: { configId: string; runId: string }) =>
      syncConfigService.requestRunReport(configId, runId).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SYNC_RUNS_KEY] });
    },
  });
};

export const useSyncRunReportDownloadUrl = () => {
  return useMutation({
    mutationFn: ({ configId, runId }: { configId: string; runId: string }) =>
      syncConfigService.getRunReportDownloadUrl(configId, runId).then((res) => res.data),
  });
};
